// ────────────────────────────────────────────────────────────────────────────
// Layout A — the flow.
//
// States as a chain, transitions as the arrows between them, and the rules
// attached to the arrow you click. This is how a workflow gets drawn on a
// whiteboard, so it needs no explaining; the cost is that it only stays legible
// while the flow is roughly linear. Cancelled is drawn as an escape hatch below
// rather than as a sixth box, because every state reaches it and drawing all
// those arrows would be noise.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { ChevronRight } from 'lucide-react';
import {
  abandonsRecord,
  transitionTitle,
  activeRequirements,
  blockingCount,
  outgoing,
  type Workflow,
  type WorkflowState,
  type WorkflowTransition,
} from '../lib/mockWorkflow';
import { CancelConditionsCard } from '../components/CancelConditionsCard';
import { RequirementList } from '../components/RequirementList';
import { StateBehaviourCard } from '../components/StateBehaviourCard';
import { TransitionPreview } from '../components/TransitionPreview';

export function FlowLayout({
  workflow,
  selected,
  onSelect,
  onChangeTransition,
  onChangeCancelRequirements,
  onChangeState,
  onChangeInvoiceableFrom,
  referenceLabels,
}: {
  workflow: Workflow;
  selected: WorkflowTransition | null;
  onSelect: (t: WorkflowTransition) => void;
  onChangeTransition: (next: WorkflowTransition) => void;
  /** Edits the track-wide cancellation policy. */
  onChangeCancelRequirements: (next: WorkflowTransition) => void;
  onChangeState: (next: WorkflowState) => void;
  onChangeInvoiceableFrom: (key: string, value: boolean) => void;
  /** The organisation's order reference labels, positional. */
  referenceLabels: string[];
}) {
  const chain = workflow.states.filter((s) => s.key !== 'cancelled');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      {/* The chain. Horizontal on wide screens, wrapping on narrow ones — read
          top-to-bottom it loses the sense of forward motion. */}
      <div className="flex flex-wrap items-stretch gap-1 rounded-lg border border-slate-200 bg-white p-4">
        {chain.map((state, i) => {
          const next = chain[i + 1];
          const transition = next
            ? outgoing(workflow, state.key).find((t) => t.to === next.key)
            : undefined;

          return (
            <div key={state.key} className="flex items-stretch gap-1">
              <div
                className={cn(
                  'flex min-w-[8.5rem] flex-col justify-between rounded-md border px-3 py-2',
                  state.terminal ? 'border-slate-300 bg-slate-50' : 'border-slate-200',
                )}
              >
                <p className="text-sm font-medium text-slate-800">{state.label}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                  {state.behaviour.plannable ? 'Plannable' : 'Not plannable'}
                </p>
              </div>

              {transition && (
                <button
                  type="button"
                  onClick={() => onSelect(transition)}
                  aria-label={`Rules for ${state.label} to ${next?.label}`}
                  className={cn(
                    'group flex flex-col items-center justify-center rounded-md px-2 py-1 transition-colors',
                    selected?.from === transition.from && selected?.to === transition.to
                      ? 'bg-sky-50 ring-1 ring-sky-300'
                      : 'hover:bg-slate-50',
                  )}
                >
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
                  {/* The count IS the information at a glance: how hard is this
                      gate? Zero conditions is a free move. */}
                  <span
                    className={cn(
                      'mt-0.5 rounded-full px-1.5 text-[10px] font-medium',
                      blockingCount(transition) > 0
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {activeRequirements(transition).length || '—'}
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {selected ? (
        <TransitionDetail
          workflow={workflow}
          transition={selected}
          onChangeTransition={onChangeTransition}
          onChangeCancelRequirements={onChangeCancelRequirements}
          onChangeState={onChangeState}
          onChangeInvoiceableFrom={onChangeInvoiceableFrom}
          referenceLabels={referenceLabels}
        />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
          Pick an arrow to see what has to be true before an order may make that move.
        </p>
      )}
    </div>
  );
}

function TransitionDetail({
  workflow,
  transition,
  onChangeTransition,
  onChangeCancelRequirements,
  onChangeState,
  onChangeInvoiceableFrom,
  referenceLabels,
}: {
  workflow: Workflow;
  transition: WorkflowTransition;
  onChangeTransition: (next: WorkflowTransition) => void;
  onChangeCancelRequirements: (next: WorkflowTransition) => void;
  onChangeState: (next: WorkflowState) => void;
  onChangeInvoiceableFrom: (key: string, value: boolean) => void;
  referenceLabels: string[];
}) {
  const target = workflow.states.find((s) => s.key === transition.to);

  // Two columns, and the pairing is the point: the LEFT column is everything
  // about the move that was picked — what has to be true for it, then what the
  // state it lands in behaves like. The RIGHT column is everything about
  // leaving the flow, then the sample records both columns are judged against.
  //
  // Placed on an explicit 2x2 grid rather than as two stacked columns, so the
  // pair in each row cannot drift apart as one of them grows a condition.
  //
  // The cards STRETCH to the row height (`[&>div]:h-full` reaches the card
  // inside each placement wrapper). Unequal content leaves white space either
  // way — the question is only whether it sits inside a card or as a gap
  // between them, and a bordered box lining up on all four edges reads as
  // deliberate where a floating gap reads as a bug.
  //
  // DOM order is the single-column reading order (below xl, where the grid
  // collapses): conditions, behaviour, cancellation, samples.
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <div className="[&>div]:h-full xl:col-start-1 xl:row-start-1">
        <RequirementList
          subject={workflow.subject}
          title={transitionTitle(workflow, transition)}
          abandons={abandonsRecord(workflow, transition)}
          transition={transition}
          referenceLabels={referenceLabels}
          onChange={onChangeTransition}
        />
      </div>
      {target && (
        <div className="[&>div]:h-full xl:col-start-1 xl:row-start-2">
          <StateBehaviourCard
            workflow={workflow}
            state={target}
            onChange={onChangeState}
            onChangeInvoiceableFrom={onChangeInvoiceableFrom}
          />
        </div>
      )}
      <div className="[&>div]:h-full xl:col-start-2 xl:row-start-1">
        <CancelConditionsCard
          workflow={workflow}
          referenceLabels={referenceLabels}
          onChange={onChangeCancelRequirements}
        />
      </div>
      <div className="[&>div]:h-full xl:col-start-2 xl:row-start-2">
        <TransitionPreview
          workflow={workflow}
          transition={transition}
          referenceLabels={referenceLabels}
        />
      </div>
    </div>
  );
}
